import React from "react";
import styled from "styled-components";
import arrowTopRight from "../assets/arrow-top-right.png";
import { motion, useMotionValue, animate } from "framer-motion";
import COLORS from "../colors";

function MobileProjectDrawer({ open, onClose, projectInfo }) {
  const y = useMotionValue(window.innerHeight);

  // Prevent body scroll when drawer is open (cleaner approach)
  React.useEffect(() => {
    if (open) {
      const preventScroll = (e) => {
        // Allow scrolling within the drawer content
        const drawerContent = e.target.closest("[data-drawer-content]");
        if (!drawerContent) {
          e.preventDefault();
        }
      };

      // Prevent scroll on touch devices
      document.addEventListener("touchmove", preventScroll, { passive: false });

      // Cleanup
      return () => {
        document.removeEventListener("touchmove", preventScroll);
      };
    }
  }, [open]);

  // Prevent click inside drawer from closing
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDragEnd = (event, info) => {
    // If dragged down more than 100px, close the drawer
    if (info.offset.y > 100) {
      onClose();
    } else {
      // Snap back to original position with smooth spring
      animate(y, 0, {
        type: "spring",
        damping: 30,
        stiffness: 400,
        mass: 0.8,
        restDelta: 0.01,
        restSpeed: 0.01,
      });
    }
  };

  return (
    <motion.div
      initial={false}
      animate={{
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
      }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1.0],
      }}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.18)",
        display: "flex",
        alignItems: "flex-end",
      }}
      onClick={handleOverlayClick}
    >
      <motion.div
        initial={false}
        animate={{ y: open ? 0 : "100%" }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 400,
          mass: 0.8,
          restDelta: 0.01,
          restSpeed: 0.01,
        }}
        style={{
          width: "100vw",
          background: "#EEEEEE",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: "0 -4px 32px rgba(0, 0, 0, 0.18)",
          minHeight: 180,
          maxHeight: "75vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          overflow: "visible",
          marginBottom: -50,
          y,
        }}
      >
        <motion.div
          onPanStart={() => {}}
          onPan={(event, info) => {
            // Only allow dragging down (positive y values)
            if (info.offset.y >= 0) {
              y.set(info.offset.y);
            }
          }}
          onPanEnd={handleDragEnd}
          style={{
            cursor: "grab",
            paddingTop: 16,
            paddingBottom: 18,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <MobileDrawerGrabber />
        </motion.div>
        {projectInfo.type === "Writing" ? (
          <MobileDrawerInner data-drawer-content>
            <ProjectTitleRow>
              <MobileProjectTitle>
                {projectInfo.source && `${projectInfo.source}`}
              </MobileProjectTitle>
              <ProjectLink
                href={projectInfo.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArrowIcon src={arrowTopRight} alt="Open in new tab" />
              </ProjectLink>
            </ProjectTitleRow>
            <MobileWritingTitle>{projectInfo.title}</MobileWritingTitle>
          </MobileDrawerInner>
        ) : (
          <MobileDrawerInner data-drawer-content>
            <ProjectTitleRow>
              <MobileProjectTitle>{projectInfo.title}</MobileProjectTitle>
              {projectInfo.link && (
                <ProjectLink
                  href={projectInfo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ArrowIcon src={arrowTopRight} alt="Open in new tab" />
                </ProjectLink>
              )}
            </ProjectTitleRow>
            <MobileProjectSubtitle>
              {projectInfo.role && `${projectInfo.role} | `}
              {projectInfo.source && `${projectInfo.source}`}
              {projectInfo.year && ` | ${projectInfo.year}`}
            </MobileProjectSubtitle>
            <MobileProjectDescription>
              {projectInfo.description}
            </MobileProjectDescription>

            <MobileDrawerImages>
              {(projectInfo.images && projectInfo.images.length > 0
                ? projectInfo.images
                : [null]
              ).map((img, idx) =>
                img ? (
                  <MobileDrawerImage
                    key={idx}
                    src={img.url || img}
                    alt={projectInfo.title + " image " + (idx + 1)}
                  />
                ) : (
                  <MobileDrawerImagePlaceholder key={idx} />
                )
              )}
            </MobileDrawerImages>
          </MobileDrawerInner>
        )}
      </motion.div>
    </motion.div>
  );
}

export default MobileProjectDrawer;

const MobileDrawerGrabber = styled.div`
  width: 40%;
  min-height: 6px;
  background: #d1d1d6;
  border-radius: 10px;
`;

const MobileDrawerInner = styled.div`
  padding: 25px 20px 25px 20px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  gap: 8px;
  display: flex;
  flex-direction: column;
  padding-bottom: 100px;
`;

const MobileDrawerImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 18px;
`;

const MobileDrawerImagePlaceholder = styled.div`
  width: 100%;
  height: auto;
  border-radius: 4px;
`;

const MobileDrawerImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
`;

const ProjectTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
`;

const MobileProjectTitle = styled.div`
  font-size: 20px;
  font-weight: 400;
  font-family: forma-djr-deck, sans-serif;
`;
const MobileProjectSubtitle = styled.div`
  font-family: forma-djr-deck, sans-serif;
  font-weight: 400;
  letter-spacing: 0.025em;
  font-size: 16px;
  line-height: 20px;
  color: ${COLORS.darkGray};
`;

const MobileProjectDescription = styled.div`
  font-family: forma-djr-deck, sans-serif;
  font-weight: 400;
  letter-spacing: 0.02em;
  font-size: 16px;
  line-height: 20px;
`;

const ProjectLink = styled.a`
  color: inherit;
  text-decoration: none;
  font-family: forma-djr-deck, sans-serif;
  &:hover {
    text-decoration: underline;
  }
`;

const ArrowIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 6px;
  vertical-align: middle;
`;

const MobileWritingTitle = styled.div`
  font-weight: 300;
  margin: 125px 0;
  font-style: italic;
  font-size: 50px;
  line-height: 60px;
`;
